import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { RocketIcon, ShieldCheckIcon, TargetIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const PRIORITIES = [{
  stage: 'A. Launch',
  title: 'Accelerate time to market',
  desc: 'Patent-protected time is finite. Build market share fast — time and share lost at launch cannot be recovered at the end.',
  icon: RocketIcon,
  surface: 'border-[var(--accent)]/30 bg-[var(--accent)]/[0.06]',
  chip: 'bg-[var(--accent)]/15 text-[var(--accent)]'
}, {
  stage: 'B. Growth / Mature',
  title: 'Win share of pocket',
  desc: 'Increase relevance, adoption, and frequency to capture more of each customer’s available spend during the growth years.',
  icon: TargetIcon,
  surface: 'border-[var(--brand-light)]/30 bg-[var(--brand-light)]/[0.06]',
  chip: 'bg-[var(--brand-light)]/15 text-[var(--brand-light)]'
}, {
  stage: 'C. Foundational Brands',
  title: 'Protect and extend value',
  desc: 'Reduce erosion, increase profitability, and prolong the commercial lifespan once exclusivity and investment decline.',
  icon: ShieldCheckIcon,
  surface: 'border-[var(--brand)]/25 bg-[var(--brand)]/[0.05]',
  chip: 'bg-[var(--brand)]/12 text-[var(--brand)]'
}] as const;
const MAIN_CURVE = 'M 80 290 C 140 290 170 380 230 380 C 290 380 340 340 380 290 C 410 250 430 130 460 90 C 540 70 650 70 730 90 C 745 95 760 245 785 275 C 850 283 920 287 960 290';
const POTENTIAL_CURVE = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268';
const POTENTIAL_AREA = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268 L 960 290 C 920 287 850 283 785 275 C 760 245 745 95 730 90 Z';
const PHASES = [{
  label: 'A. Launch',
  sub: 'Speed to market',
  x: 420,
  zoneX: 380,
  zoneWidth: 80,
  color: '#6A2C91'
}, {
  label: 'B. Growth / Mature',
  sub: 'Share of pocket',
  x: 595,
  zoneX: 460,
  zoneWidth: 270,
  color: '#C2410C'
}, {
  label: 'C. Foundational Brands',
  sub: 'Protect and extend',
  x: 845,
  zoneX: 730,
  zoneWidth: 230,
  color: '#F36633'
}];
const AXIS = '#D8DEE7';
export function SlideLifecycleTail({}: SlideProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [setTimeout(() => setStage(1), 600), setTimeout(() => setStage(2), 1400), setTimeout(() => setStage(3), 4000), setTimeout(() => setStage(4), 5800), setTimeout(() => setStage(5), 6600)];
    return () => timers.forEach(clearTimeout);
  }, []);
  return <InteriorSlide data-id="element-334" data-mp="2g3uwjg78l86">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="text-center flex-shrink-0" data-id="element-335" data-mp="3tkanrlga20tf">
        <p className="text-[var(--brand)] text-[clamp(0.7rem,1vw,0.9rem)] font-semibold uppercase tracking-[0.18em] mb-2" data-id="element-336" data-mp="7exb4jmveqc1">
          Commercial Lifecycle Strategy
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(1.75rem,3.2vw,2.9rem)] leading-tight tracking-tight" data-id="element-337" data-mp="uz6d1xqq15um">
          Three priorities across the product lifecycle
        </h2>
      </motion.div>

      <div className="flex-1 min-h-0 relative flex items-center justify-center py-2" data-id="element-338" data-mp="lyhvit24cncz">
        <div className="w-full max-w-5xl max-h-full relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-339" data-mp="79u4n037x1td">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-340" data-mp="c3gbgbmpabh9">
            <defs data-id="element-341" data-mp="3udi443lr93ab">
              <pattern id="lifecycle-hatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)" data-id="element-342" data-mp="39bbn2pl9ppl">
                <line x1="0" y1="0" x2="0" y2="8" stroke="#F36633" strokeWidth="1.5" strokeOpacity="0.45" data-id="element-343" data-mp="3ph1ef4ywqgv4" />
              </pattern>
            </defs>

            {/* Axis annotations */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-344" data-mp="mux5eoc6pr81">
              <text x="60" y="100" textAnchor="middle" className="text-xs font-medium" fill="#9CA3AF" data-id="element-345" data-mp="8vxh5hsl3ece">
                +
              </text>
              <text x="60" y="385" textAnchor="middle" className="text-xs font-medium" fill="#9CA3AF" data-id="element-346" data-mp="3v587gppsuo6a">
                −
              </text>
              <text x="30" y="240" textAnchor="middle" className="text-xs font-medium" fill="#6B7280" style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed'
            }} data-id="element-347" data-mp="20vt9pyijjfaa">
                Cumulative Value
              </text>
              <text x="520" y="455" textAnchor="middle" className="text-xs font-medium" fill="#6B7280" data-id="element-348" data-mp="2fmsh567n3xsa">
                Time
              </text>
              <text x="975" y="295" className="text-[10px] font-medium" fill="#9CA3AF" data-id="element-349" data-mp="1ruveo5q2yi3u">
                €0
              </text>
            </motion.g>

            {/* Y axis */}
            <motion.line x1="80" y1="40" x2="80" y2="430" stroke={AXIS} strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-350" data-mp="33ak2ezb5rl5p" />

            {/* Zero line */}
            <motion.line x1="80" y1="290" x2="970" y2="290" stroke={AXIS} strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.8
          }} data-id="element-351" data-mp="1h7a5rn6acb3m" />

            {/* Phase dividers */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-352" data-mp="thzaumbm9axe">
              {[380, 460, 730].map(x => <line key={x} x1={x} y1="50" x2={x} y2="430" stroke={AXIS} strokeWidth="1" strokeDasharray="4,4" data-id="element-353" data-mp="3nrhal4eg8txe" />)}
            </motion.g>

            {/* Commercial phase highlights */}
            {PHASES.map((phase, phaseIndex) => <motion.rect key={phase.label} x={phase.zoneX} y="50" width={phase.zoneWidth} height="380" fill={phase.color} fillOpacity="0.055" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5,
            delay: phaseIndex * 0.12
          }} data-id="element-354" data-mp="ae5lx1dxd79e" data-mpi={phaseIndex} />)}

            {/* Established-stage extension opportunity */}
            <motion.path d={POTENTIAL_AREA} fill="url(#lifecycle-hatch)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1
          }} data-id="element-355" data-mp="2q2npv6h6vdr7" />

            {/* Main curve */}
            <motion.path d={MAIN_CURVE} fill="none" stroke="#1A1A1A" strokeWidth="3" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 2.6,
            ease: 'easeInOut'
          }} data-id="element-356" data-mp="1tg11qa7y806h" />

            {/* Potential extension */}
            <motion.path d={POTENTIAL_CURVE} fill="none" stroke="#F36633" strokeWidth="3" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.4,
            ease: 'easeInOut'
          }} data-id="element-357" data-mp="38vgm3o58idks" />

            {/* Phase labels */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-358" data-mp="14llr7o24hpry">
              {PHASES.map(phase => <g key={phase.label} data-id="element-359" data-mp="1jarov7lbb7xq">
                  <text x={phase.x} y="450" textAnchor="middle" className="text-sm font-semibold" fill={phase.color} data-id="element-360" data-mp="1jbt0pea5jygi">
                    {phase.label}
                  </text>
                  <text x={phase.x} y="468" textAnchor="middle" className="text-xs" fill={phase.color} data-id="element-361" data-mp="1xb2gicug4qjy">
                    {phase.sub}
                  </text>
                </g>)}
              <text x="230" y="410" textAnchor="middle" className="text-[10px] italic" fill="#9CA3AF" data-id="element-362" data-mp="150mmk2zv9yui">
                Pre-launch investment
              </text>
              <text x="595" y="55" textAnchor="middle" className="text-[10px] italic" fill="#9CA3AF" data-id="element-363" data-mp="1jqkcandg2wlt">
                Peak Revenue
              </text>
            </motion.g>

            {/* Potential callout */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} data-id="element-364" data-mp="3qibf41nauy26">
              <line x1="845" y1="215" x2="900" y2="178" stroke="#F36633" strokeWidth="1" strokeOpacity="0.6" data-id="element-365" data-mp="3i8nia07ciiq7" />
              <text x="908" y="170" className="text-sm font-bold tracking-wider" fill="#F36633" data-id="element-366" data-mp="39a24eictjrvr">
                POTENTIAL
              </text>
              <text x="908" y="186" className="text-[10px]" fill="#C2410C" data-id="element-367" data-mp="4s6enarwpak7">
                Softening the cliff
              </text>
            </motion.g>
          </svg>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 5 ? 1 : 0,
      y: stage >= 5 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto w-full flex-shrink-0 mt-4" data-id="element-368" data-mp="3s11n51tq05gl">
        {PRIORITIES.map(priority => {
        const Icon = priority.icon;
        return <article key={priority.stage} className={`rounded-2xl border p-4 ${priority.surface}`} data-id="element-369" data-mp="28z95knh4hiw9">
              <div className="flex items-center gap-2.5 mb-2" data-id="element-370" data-mp="2d3gwmrb1zaxn">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${priority.chip}`} data-id="element-371" data-mp="1z9fxhwg6vlwr">
                  <Icon className="h-4 w-4" aria-hidden="true" data-id="element-372" data-mp="1pcbjijwhywr1" />
                </div>
                <div data-id="element-373" data-mp="1ub4k5bqoqt9s">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--muted)]" data-id="element-374" data-mp="1ualf2vm4vzd6">
                    {priority.stage}
                  </p>
                  <h3 className="text-[var(--fg)] font-bold text-sm" data-id="element-375" data-mp="1gdg90vmf70hx">
                    {priority.title}
                  </h3>
                </div>
              </div>
              <p className="text-[var(--muted)] text-[12px] leading-snug" data-id="element-376" data-mp="11p6j9gcvzqjh">
                {priority.desc}
              </p>
            </article>;
      })}
      </motion.div>
    </InteriorSlide>;
}